import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X, Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionHead } from "../components/Reveal";
import Seo from "../components/Seo";
import { API, INPUT_CLS } from "../data";

const FILTERS = ["Tutte", "Ristrutturazione", "Impianti", "Nuova Costruzione"];
const SERVICES_OPTS = ["Ristrutturazione", "Impianti", "Nuova Costruzione", "Altro"];

const Stars = ({ value, onChange, size = "h-4 w-4" }) => (
  <div className="flex gap-1">
    {Array.from({ length: 5 }).map((_, i) => (
      <button
        key={i}
        type={onChange ? "button" : undefined}
        onClick={onChange ? () => onChange(i + 1) : undefined}
        data-testid={onChange ? `star-input-${i + 1}` : undefined}
        className={onChange ? "cursor-pointer transition-transform hover:scale-110" : "cursor-default"}
        aria-label={`${i + 1} stelle`}
      >
        <Star className={`${size} ${i < value ? "fill-gold text-gold" : "text-slate-600"}`} />
      </button>
    ))}
  </div>
);

export default function Recensioni() {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("Tutte");
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ nome: "", servizio: "Ristrutturazione", rating: 5, testo: "" });

  useEffect(() => {
    axios.get(`${API}/recensioni`).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const visible = filter === "Tutte" ? reviews : reviews.filter((r) => r.servizio === filter);
  const avg = reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : "—";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/recensioni`, form);
      setReviews([data, ...reviews]);
      setOpen(false);
      setForm({ nome: "", servizio: "Ristrutturazione", rating: 5, testo: "" });
      toast.success("Grazie per la tua recensione!", { description: "La tua testimonianza è stata pubblicata." });
    } catch {
      toast.error("Errore nell'invio", { description: "Riprova tra poco o chiamaci direttamente." });
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="recensioni-page" className="pt-20">
      <Seo
        title="Recensioni dei Clienti | Edilkrea Technoimpianti"
        description="Leggi le recensioni dei clienti di Edilkrea Technoimpianti su ristrutturazioni e impianti, e racconta la tua esperienza con i nostri cantieri."
      />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 sm:pt-24 pb-10">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <SectionHead level="h1" overline="Testimonianze" title="Cosa Dicono i Clienti" />
            <Reveal delay={0.15} className="mt-6 flex items-center gap-4">
              <span className="font-display text-5xl font-extrabold text-gold" data-testid="reviews-average">{avg}</span>
              <div>
                <Stars value={Math.round(Number(avg) || 0)} size="h-5 w-5" />
                <p className="mt-1 text-xs uppercase tracking-widest text-slate-400" data-testid="reviews-count">
                  {reviews.length} recensioni verificate
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <button
              data-testid="review-add-button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-ink transition-all hover:bg-copper hover:-translate-y-0.5"
            >
              <Plus className="h-4 w-4" /> Lascia una Recensione
            </button>
          </Reveal>
        </div>

        <Reveal delay={0.25} className="mt-10 flex flex-wrap gap-2" y={16}>
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`review-filter-${f.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all ${
                filter === f ? "bg-gold text-ink" : "border border-gold/20 text-cream/70 hover:border-gold/50 hover:text-gold"
              }`}
            >
              {f}
            </button>
          ))}
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-32">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="reviews-grid">
          {visible.map((r, i) => (
            <Reveal key={r.id} delay={(i % 3) * 0.08}>
              <figure className="h-full rounded-2xl border border-gold/15 bg-card2 p-6 transition-colors hover:border-gold/40" data-testid={`review-card-${i}`}>
                <Stars value={r.rating} />
                <blockquote className="mt-4 text-sm leading-relaxed text-slate-300">"{r.testo}"</blockquote>
                <figcaption className="mt-5 flex items-center justify-between">
                  <span className="font-display font-bold text-cream">{r.nome}</span>
                  <span className="font-mono text-[11px] uppercase tracking-widest text-copper">{r.servizio}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        {visible.length === 0 && (
          <p className="py-16 text-center text-slate-400" data-testid="reviews-empty">Nessuna recensione in questa categoria.</p>
        )}
      </section>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="review-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/90 backdrop-blur-md p-4"
            onClick={() => setOpen(false)}
          >
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-lg rounded-2xl border border-gold/20 bg-panel p-6 sm:p-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="review-form"
            >
              <button type="button" data-testid="review-modal-close" onClick={() => setOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-copper" aria-label="Chiudi">
                <X className="h-5 w-5" />
              </button>
              <h3 className="font-display text-2xl font-bold uppercase text-cream">La Tua Esperienza</h3>
              <div className="mt-6 space-y-4">
                <input
                  data-testid="review-input-nome"
                  required
                  placeholder="Nome e cognome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className={INPUT_CLS}
                />
                <select
                  data-testid="review-input-servizio"
                  value={form.servizio}
                  onChange={(e) => setForm({ ...form, servizio: e.target.value })}
                  className={INPUT_CLS}
                >
                  {SERVICES_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-400">Valutazione:</span>
                  <Stars value={form.rating} onChange={(rating) => setForm({ ...form, rating })} size="h-6 w-6" />
                </div>
                <textarea
                  data-testid="review-input-testo"
                  required
                  rows={4}
                  placeholder="Raccontaci come è andata..."
                  value={form.testo}
                  onChange={(e) => setForm({ ...form, testo: e.target.value })}
                  className={`${INPUT_CLS} resize-none`}
                />
                <button
                  type="submit"
                  data-testid="review-submit-button"
                  disabled={sending}
                  className="w-full rounded-full bg-gold py-3.5 text-sm font-semibold uppercase tracking-wider text-ink transition-all hover:bg-copper disabled:opacity-60"
                >
                  {sending ? "Invio in corso..." : "Pubblica Recensione"}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
